import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { UseInfiniteQueryResult } from 'react-query';
import classNames from 'classnames';

import Spinner from 'src/components/Spinner';
import StarRate from 'src/components/StarRate';
import { IGameReview } from 'src/interface';
import { formatDate, formatDateHowLongAgo } from 'src/utils/func/format-date';

import classes from './index.module.less';

interface Props {
	data: UseInfiniteQueryResult;
}

const ReviewList = ({ data }: Props) => {
	const observe = useInView();

	const { data: dataRL, isLoading, hasNextPage, fetchNextPage } = data;

	const [listShowMoreLess, setListShowMoreLess] = useState<
		{ id: number; state: 'more' | 'less' }[]
	>([]);

	const maxCharacterShowed = 350;

	let items: IGameReview[] = [];
	dataRL?.pages?.forEach((page: any) => (items = [...items, ...(page?.data?.data?.data ?? [])]));

	const isEmpty = !isLoading && items?.length === 0;

	const handleMoreLessClick = (id: number, state: 'more' | 'less') => {
		const index = listShowMoreLess?.findIndex((r) => r.id === id);
		if (index > -1) {
			let newListShowMoreLess = [...listShowMoreLess];
			if (state === 'more') {
				newListShowMoreLess[index] = { ...newListShowMoreLess[index], state: 'less' };
			} else {
				newListShowMoreLess[index] = { ...newListShowMoreLess[index], state: 'more' };
			}
			setListShowMoreLess(newListShowMoreLess);
		}
	};

	useEffect(() => {
		if (observe.inView) {
			if (fetchNextPage) {
				fetchNextPage();
			}
		}
	}, [observe.inView]);

	useEffect(() => {
		items?.forEach((r: IGameReview) => {
			const index = listShowMoreLess.findIndex(({ id }) => r.id === id);

			if (r.comment.length > maxCharacterShowed) {
				if (index <= -1) {
					let newListShowMoreLess = [...listShowMoreLess];
					newListShowMoreLess.push({ id: r.id, state: 'more' });
					setListShowMoreLess(newListShowMoreLess);
				}
			} else {
				if (index > -1) {
					let newListShowMoreLess = [...listShowMoreLess];
					newListShowMoreLess.splice(index, 1);
					setListShowMoreLess(newListShowMoreLess);
				}
			}
		});
	}, [JSON.stringify(items)]);

	return (
		<>
			{isEmpty ? (
				<div className={classNames(classes['card-no-review'])}>No Review Yet</div>
			) : (
				<div className={classes['review-list-container']}>
					<div className={classes.content}>
						{items?.map((r: IGameReview, i: number) => {
							const showMoreLess = listShowMoreLess?.find(({ id }) => id === r.id);

							return (
								<div key={r.id + '-' + i} className={classes.card}>
									<StarRate className={classes.rating} ratingNumber={r.star} />
									<div className={classes.comment}>
										{showMoreLess && showMoreLess?.state === 'more'
											? r.comment.substring(0, maxCharacterShowed)
											: r.comment}{' '}
										{showMoreLess && (
											<>
												{showMoreLess.state === 'more' ? (
													<a onClick={() => handleMoreLessClick(r.id, 'more')}>Read More</a>
												) : (
													<a onClick={() => handleMoreLessClick(r.id, 'less')}>Read Less</a>
												)}
											</>
										)}
									</div>
									<div className={classes['review-info']}>
										<span>{r.is_username_hidden ? 'Anonymous' : r.user.username}</span>
										<span>
											{formatDate(r.created_at, 'MMM D, YYYY')},{' '}
											{formatDateHowLongAgo(r.created_at)}
										</span>
									</div>
								</div>
							);
						})}
						{!isEmpty && hasNextPage && (
							<div className="text-center" ref={observe.ref}>
								<Spinner />
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default ReviewList;
